import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Receipt, Search } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { INVOICE_STATUSES, StatusBadge, money, fmtDate } from "@/lib/format";
import MobileListCard from "@/components/MobileListCard";
import SwipeableListItem from "@/components/SwipeableListItem";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initial = params.get("status") || "";
    const [list, setList] = useState(null);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(initial);

    const fetchList = async () => {
        setErr("");
        try {
            const { data } = await api.get("/invoices", {
                params: {
                    ...(statusFilter ? { status_filter: statusFilter } : {}),
                    ...(q.trim() ? { q: q.trim() } : {}),
                },
            });
            setList(data.invoices);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        fetchList();
        const next = new URLSearchParams(params);
        if (statusFilter) next.set("status", statusFilter);
        else next.delete("status");
        setParams(next, { replace: true });
        // eslint-disable-next-line
    }, [statusFilter]);

    const counts = useMemo(() => {
        const c = { all: list?.length || 0 };
        INVOICE_STATUSES.forEach((s) => (c[s] = 0));
        (list || []).forEach((l) => (c[l.status] = (c[l.status] || 0) + 1));
        return c;
    }, [list]);

    const outstanding = useMemo(
        () =>
            (list || []).reduce(
                (sum, i) => sum + (i.status !== "paid" ? Number(i.balance_due || 0) : 0),
                0,
            ),
        [list],
    );

    return (
        <div className="page-shell">
            <header className="fade-up flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="eyebrow">Invoices</p>
                    <h1 className="h1 mt-3" data-testid="invoices-title">
                        Get paid
                    </h1>
                    <p className="text-muted mt-2 max-w-[640px]">
                        Track every dollar from issue to clearing. Overdue invoices auto-mark
                        themselves so nothing slips.
                    </p>
                </div>
                <div
                    className="brand-card text-right"
                    style={{ padding: "0.85rem 1.25rem" }}
                    data-testid="invoices-outstanding-card"
                >
                    <p className="eyebrow" style={{ justifyContent: "flex-end" }}>
                        Outstanding
                    </p>
                    <p
                        className="font-display font-bold font-mono mt-1"
                        style={{ fontSize: "1.5rem", color: "var(--brand-red)" }}
                        data-testid="invoices-outstanding"
                    >
                        {money(outstanding)}
                    </p>
                </div>
            </header>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${statusFilter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setStatusFilter("")}
                    data-testid="inv-filter-all"
                >
                    All ({counts.all})
                </button>
                {INVOICE_STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`btn ${statusFilter === s ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setStatusFilter(s)}
                        data-testid={`inv-filter-${s}`}
                    >
                        <span className="capitalize">{s}</span>
                        <span className="text-xs opacity-70">({counts[s] || 0})</span>
                    </button>
                ))}
            </div>

            <form
                className="mt-4 flex gap-2 flex-wrap"
                onSubmit={(e) => {
                    e.preventDefault();
                    fetchList();
                }}
            >
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        placeholder="Search by client or invoice #"
                        className="field-input"
                        style={{ paddingLeft: "2.25rem" }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="inv-search-input"
                    />
                </div>
                <button type="submit" className="btn btn-secondary" data-testid="inv-search-btn">
                    Search
                </button>
            </form>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="invoices-error"
                >
                    {err}
                </div>
            )}

            <div className="brand-card mt-6 p-0 overflow-hidden fade-up" data-testid="invoices-card">
                {list === null ? (
                    <div className="p-8 text-muted">Loading invoices…</div>
                ) : list.length === 0 ? (
                    <div className="p-10 text-center">
                        <Receipt
                            size={28}
                            style={{ color: "var(--brand-muted)" }}
                            className="mx-auto"
                        />
                        <p className="text-muted mt-3" data-testid="invoices-empty">
                            No invoices yet. Complete a job and generate one.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="sm:hidden p-3" data-testid="invoices-cards">
                            {list.map((i) => {
                                const balanceDue = Number(i.balance_due);
                                const canMarkPaid = balanceDue > 0 && i.status !== "paid";
                                const action = canMarkPaid
                                    ? {
                                          label: "Mark Paid",
                                          color: "green",
                                          icon: <CheckCircle2 size={16} />,
                                          onClick: async () => {
                                              try {
                                                  const { data } = await api.post(
                                                      `/invoices/${i.id}/payment`,
                                                      {
                                                          amount: balanceDue,
                                                          method: "check",
                                                          note: "Marked paid via swipe action",
                                                      },
                                                  );
                                                  const paymentId = data?.payment_id;
                                                  await fetchList();
                                                  // Undo toast — 4 seconds to recover a fumble
                                                  toast.success(
                                                      `${i.invoice_number} marked paid · ${money(balanceDue)}`,
                                                      {
                                                          duration: 4000,
                                                          action: paymentId
                                                              ? {
                                                                    label: "Undo",
                                                                    onClick: async () => {
                                                                        try {
                                                                            await api.delete(
                                                                                `/invoices/${i.id}/payment/${paymentId}`,
                                                                            );
                                                                            await fetchList();
                                                                            toast("Payment reversed");
                                                                        } catch (undoErr) {
                                                                            toast.error(
                                                                                apiErrorMessage(
                                                                                    undoErr,
                                                                                    "Could not undo",
                                                                                ),
                                                                            );
                                                                        }
                                                                    },
                                                                }
                                                              : undefined,
                                                      },
                                                  );
                                              } catch (err) {
                                                  setErr(
                                                      apiErrorMessage(
                                                          err,
                                                          "Could not record payment",
                                                      ),
                                                  );
                                              }
                                          },
                                      }
                                    : null;
                                return (
                                    <SwipeableListItem key={i.id} action={action}>
                                        <MobileListCard
                                            to={`/invoices/${i.id}`}
                                            testId={`inv-card-${i.id}`}
                                            title={i.invoice_number}
                                            subtitle={i.client?.name}
                                            amount={money(i.grand_total)}
                                            right={<StatusBadge status={i.status} />}
                                            meta={[
                                                { label: "Job", value: i.job_number },
                                                { label: "Due", value: fmtDate(i.due_date) },
                                                {
                                                    label: "Balance",
                                                    value: (
                                                        <span
                                                            style={{
                                                                color:
                                                                    balanceDue > 0
                                                                        ? "var(--brand-red)"
                                                                        : "var(--brand-success)",
                                                            }}
                                                        >
                                                            {money(i.balance_due)}
                                                        </span>
                                                    ),
                                                },
                                            ]}
                                        />
                                    </SwipeableListItem>
                                );
                            })}
                        </div>
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="brand-table" data-testid="invoices-table">
                            <thead>
                                <tr>
                                    <th>Number</th>
                                    <th>Client</th>
                                    <th>Job</th>
                                    <th className="text-right">Total</th>
                                    <th className="text-right">Balance</th>
                                    <th>Due</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((i) => (
                                    <tr
                                        key={i.id}
                                        onClick={() => navigate(`/invoices/${i.id}`)}
                                        data-testid={`inv-row-${i.id}`}
                                    >
                                        <td>
                                            <p className="font-display font-bold">
                                                {i.invoice_number}
                                            </p>
                                            <p className="text-xs text-muted">
                                                {fmtDate(i.created_at)}
                                            </p>
                                        </td>
                                        <td>{i.client?.name}</td>
                                        <td className="text-sm text-muted">{i.job_number}</td>
                                        <td className="text-right font-mono">
                                            {money(i.grand_total)}
                                        </td>
                                        <td
                                            className="text-right font-mono"
                                            style={{
                                                color:
                                                    Number(i.balance_due) > 0
                                                        ? "var(--brand-red)"
                                                        : "inherit",
                                            }}
                                        >
                                            {money(i.balance_due)}
                                        </td>
                                        <td className="text-sm text-muted">
                                            {fmtDate(i.due_date)}
                                        </td>
                                        <td>
                                            <StatusBadge status={i.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
